import { useEffect, useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { Loader } from '@/components/Loader';
import { agentApi } from '@/lib/api';
import { AGENT_LINKS } from './links';
import { toast } from 'sonner';
import { BriefcaseBusiness, Building2, Mail, Phone, ShieldCheck, UserRound } from 'lucide-react';

function Field({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-400">
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </div>
      <div className="mt-2 break-words text-base font-semibold text-[#0D1B2A]">{value || 'Non renseigné'}</div>
    </div>
  );
}

export default function AgentProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    agentApi.profile()
      .then(({ data }) => setProfile(data))
      .catch((error) => toast.error(error.response?.data?.detail || 'Impossible de charger votre profil'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppLayout links={AGENT_LINKS} title="Mon profil">
      {loading && <Loader />}
      {!loading && !profile && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">Votre profil n’a pas pu être chargé.</div>
      )}
      {profile && (
        <div className="space-y-6">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="bg-[#0D1B2A] px-6 py-7 text-white">
              <div className="flex flex-wrap items-center justify-between gap-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
                    <UserRound className="h-7 w-7" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/60">Compte agent</div>
                    <h2 className="mt-1 text-2xl font-bold">{profile.full_name || `${profile.prenom || ''} ${profile.nom || ''}`.trim()}</h2>
                    <p className="mt-1 text-sm text-white/70">{profile.poste || 'Agent LoyerBook'}</p>
                  </div>
                </div>
                <div className={`rounded-full px-3 py-1.5 text-xs font-bold ${profile.is_active ? 'bg-emerald-400/20 text-emerald-200' : 'bg-red-400/20 text-red-200'}`}>
                  {profile.is_active ? 'Compte actif' : 'Compte suspendu'}
                </div>
              </div>
            </div>
            <div className="grid gap-4 p-6 sm:grid-cols-2 xl:grid-cols-3">
              <Field label="Prénom" value={profile.prenom} icon={UserRound} />
              <Field label="Nom" value={profile.nom} icon={UserRound} />
              <Field label="Poste" value={profile.poste} icon={BriefcaseBusiness} />
              <Field label="E-mail" value={profile.email} icon={Mail} />
              <Field label="Téléphone" value={profile.telephone} icon={Phone} />
              <Field label="Bailleurs suivis" value={String(profile.portfolio_count ?? 0)} icon={Building2} />
            </div>
          </section>

          <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <div className="font-bold">Profil protégé en lecture seule</div>
              <p className="mt-1 text-blue-800">Pour corriger vos informations personnelles ou votre poste, contactez un administrateur LoyerBook. Vous pouvez modifier votre mot de passe depuis les paramètres de sécurité du compte.</p>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
